import { useEffect, useState, useCallback } from "react";
import {
  ContentTag,
  Button,
  BUTTON_VARIANTS,
  BUTTON_SIZES,
  Image,
  MediaCard,
  Alert,
  DownloadOutlineIcon,
} from "@gonoodle/gn-universe-ui";
import {
  PAGES,
  SECTIONS,
  CONTENT_TYPES,
  SECTIONS_TYPES,
} from "@gonoodle/gn-universe-analytics-schema/src/constants";
import { generatePath } from "@gonoodle/gn-universe-utils";
import fileDownload from "js-file-download";
import { QueryClient, dehydrate } from "@tanstack/react-query";
import { useParams } from "next/navigation";

import api from "../../api";
import UserSelfIdModal from "../../components/UserSelfIdModal";
import ShareToGoogleClassroomButton from "../../components/ShareToGoogleClassroomButton";
import Layout from "../../components/Layout";
import {
  QUERY_KEYS,
  ROUTE_PREFIX,
  ROUTE_PATHS,
  ROUTE_PERMISSIONS,
} from "../../constants";
import { useUser } from "../../contexts/user";
import { useLogEvent } from "../../contexts/Analytics";
import { withAuthorization } from "../../containers";
import FavoriteButton from "../../components/FavoriteButton";
import { usePrintableDetailsQuery, useRouter } from "../../hooks";
import Link from "../../components/Link";

const Activities = () => {
  const router = useRouter();
  const params = useParams();
  const [selfIdentify, setSelfIdentify] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [hasAttemptedToDownload, setHasAttemptedToDownload] = useState(false);
  const { user } = useUser();
  const alert = Alert.useAlert();

  const { identity } = params;
  const [id] = identity;

  const {
    printableDetails,
    isPrintablesDetailsLoading,
    printablesDetailsError,
  } = usePrintableDetailsQuery({
    slug: id,
  });

  useLogEvent({
    event: "Activity Viewed",
    properties: {
      contentType: CONTENT_TYPES.ACTIVITY,
      id,
      title: printableDetails?.title.toLowerCase(),
    },
    options: {
      enabled: !!printableDetails,
    },
  });

  useLogEvent({
    event: "Activity Downloaded",
    properties: {
      contentType: CONTENT_TYPES.ACTIVITY,
      id,
      title: printableDetails?.title.toLowerCase(),
    },
    options: {
      enabled: !!printableDetails && hasDownloaded,
    },
  });

  const downloadFile = useCallback((fileUrl) => {
    const filename = fileUrl.split("/").pop();
    // Create blob
    fetch(fileUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/pdf",
      },
    })
      .then((response) => response.blob())
      .then((blob) => {
        fileDownload(blob, filename);
      })
      .then(() => {
        setHasDownloaded(true);
      });
  }, []);

  const onDownload = () => {
    if (user.isAnonymous) {
      setSelfIdentify(true);
      setHasAttemptedToDownload(true);
      return;
    }

    downloadFile(printableDetails.url);
  };

  useEffect(() => {
    if (
      selfIdentify === false &&
      hasAttemptedToDownload === true &&
      !user.isAnonymous
    ) {
      downloadFile(printableDetails.url);
      setHasAttemptedToDownload(undefined);
    }
  }, [
    hasAttemptedToDownload,
    printableDetails?.url,
    selfIdentify,
    user,
    downloadFile,
  ]);

  if (isPrintablesDetailsLoading) {
    return null;
  }

  if (printablesDetailsError) {
    alert.publish("Something went wrong. Please reload page.");

    return null;
  }

  const { title, description, image, contentTags, video } = printableDetails;

  return (
    <Layout
      pageTitle={title}
      pageDescription={description}
      canonicalLinkPath={generatePath(ROUTE_PATHS.ACTIVITIES, {
        id,
        slug: printableDetails.slug,
      })}
      progressBar
    >
      <>
        <div className="container max-w-screen-xl pb-md">
          <div className="flex flex-row flex-wrap md:flex-nowrap">
            <div className="w-full md:w-2/5 md:mr-md">
              <Image className="w-full" sources={image} alt="printable cover" />
            </div>
            <div className="flex flex-col md:mt-xl md:w-3/5">
              <h4 className="gn-page-header-sm text-white pb-md">{title}</h4>
              <p className="mt-6 text-base font-normal text-white px-1">
                {description}
              </p>
              <div className="py-sm pb-lg">
                {contentTags.map(({ id: tagId, name, slug }) => (
                  <ContentTag
                    key={tagId}
                    text={name}
                    slug={slug}
                    onClick={() => {
                      const path = generatePath(ROUTE_PATHS.TAGS, {
                        id: tagId,
                        slug,
                      });

                      router.push(path);
                    }}
                  />
                ))}
              </div>
              <div className="flex flex-col space-y-sm sm:flex-row sm:space-x-sm sm:space-y-0 mb-7">
                <Button
                  variant={BUTTON_VARIANTS.light}
                  size={BUTTON_SIZES.md}
                  onPress={onDownload}
                >
                  <span className="inline-flex justify-center items-center font-bold">
                    Download
                    <DownloadOutlineIcon className="h-md ml-xs" />
                  </span>
                </Button>

                <ShareToGoogleClassroomButton
                  id={id}
                  title={title}
                  contentType={CONTENT_TYPES.ACTIVITY}
                />

                <FavoriteButton
                  variant="button"
                  type={CONTENT_TYPES.ACTIVITY}
                  id={id}
                  title={title}
                />
              </div>
              <div className="w-full md:w-5/6">
                {video && (
                  <Link
                    href={`/${ROUTE_PREFIX.VIDEOS}/${video.id}/${video.slug}`}
                    referrer={{
                      sourcePage: `${printableDetails.slug} detail page`,
                      sourcePageType: PAGES.PRINTABLE_DETAIL,
                      sourceElement: SECTIONS_TYPES.SHELF,
                      sourceName: SECTIONS.PAIRED_VIDEO,
                    }}
                  >
                    <MediaCard
                      title={video.title}
                      images={video.images}
                      description={video.description}
                      layout="wide"
                    />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
        <UserSelfIdModal
          isOpen={selfIdentify}
          onClose={() => setSelfIdentify(false)}
          analyticsProps={{
            contentType: CONTENT_TYPES.ACTIVITY,
            id,
            title: title.toLowerCase(),
          }}
        />
      </>
    </Layout>
  );
};

export const getServerSideProps = withAuthorization(async (ctx) => {
  const {
    params: { identity },
    query: { message },
  } = ctx;
  const [id] = identity;
  const client = api(ctx);
  const queryClient = new QueryClient();

  try {
    await queryClient.fetchQuery({
      queryKey: [QUERY_KEYS.PRINTABLE_DETAILS, id],
      queryFn: () => client.getPrintablesDetails(id),
    });
  } catch (error) {
    if (error?.code === 410 && !message) {
      return {
        redirect: {
          destination: "?message=410",
          permanent: true,
        },
      };
    }

    if ((error?.code === 410 && message) || error?.code === 404) {
      return { notFound: true };
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
  };
}, ROUTE_PERMISSIONS.PUBLIC);

export default Activities;
